/**
 * Minimal request/response types and sample payloads for Simulation UI.
 * Aligned with backend src/shared/types/api.ts.
 */

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
}

export interface StandardResponse {
  Success: boolean;
  InterfaceId?: string;
  OMSCustomsId?: string;
  ReferenceNo?: string | null;
  Message: string[];
}

// Minimal samples for simulation (valid shapes; backend may require more fields)

export const sampleExportDecl = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-EXP-001",
  DocumentType: "EXP",
  DeclarationType: "EXP",
  ExporterCode: "EXP001",
  ExporterTaxNumber: "1234567890123",
  ExporterBranch: "00000",
  ExporterThaiName: "บริษัท ทดสอบ",
  ExporterEnglishName: "Test Export Co",
  ExporterStreetAndNumber: "123 Main St",
  ExporterPostcode: "10110",
  Invoice: [
    {
      InvoiceNumber: "INV001",
      InvoiceDate: "2025-01-15",
      PurchaseOrderNumber: "PO001",
      TermsofPaymentCode: "01",
      TradeTerms: "FOB",
      SellerStatus: "01",
      ConsigneeCode: "CONS001",
      ConsigneeStatus: "01",
      ConsigneeName: "Consignee Ltd",
      ConsigneeStreetAndNumber: "456 Overseas Rd",
      ConsigneePostcode: "10001",
      ConsigneeCountryCode: "US",
      CommercialLevel: "01",
      InvoiceCurrencyCode: "USD",
      TotalInvoiceAmount: "1000.00",
      Detail: [
        {
          ItemNumber: "1",
          TariffCode: "1234567890",
          TariffSequence: "01",
          TariffStatistical: "00",
          ExportTariff: "0",
          ProductCode: "P001",
          GoodsThaiDescription1: "สินค้าทดสอบ",
          GoodsEnglishDescription1: "Test goods",
          NetWeight: "10",
          NetWeightUnitCode: "KGM",
          Quantity: "10",
          QuantityUnitCode: "PCE",
          DetailCurrencyCode: "USD",
          UnitPriceForeign: "100.00",
          InvoiceQuantity: "10",
          InvoiceQuantityUnitCode: "PCE",
          InvoiceAmountForeign: "1000.00",
        },
      ],
    },
  ],
} as const;

export const sampleCalculate = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-001",
} as const;

export const sampleSendExportDecl = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-EXP-001",
} as const;

export const samplePrintDocument = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-EXP-001",
  PrintLayout: "default",
} as const;

export const sampleGCL = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-GCL-001",
  CompanyCode: "COMP001",
  CompanyTaxNumber: "1234567890123",
  CompanyBranch: "00000",
  CompanyThaiName: "บริษัท ทดสอบ",
  CompanyStreetAndNumber: "123 Main St",
  CompanyPostcode: "10110",
  Detail: [
    {
      DeclarationNumber: "DEC001",
      CompanyTaxNumber: "1234567890123",
      CompanyBranch: "00000",
      PackageAmount: "1",
      PackageUnitCode: "PK",
      GrossWeight: "100",
      GrossUnitCode: "KGM",
    },
  ],
} as const;

export const sampleShort = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-SHORT-001",
  DeclarationType: "GDS",
  DeclarationNumber: "DEC001",
  CompanyTaxNumber: "1234567890123",
  CompanyBranch: "00000",
  Detail: [
    {
      InvoiceNumber: "INV001",
      ItemNumber: "1",
      TariffCode: "1234567890",
      TariffStatistical: "00",
      NetWeight: "10",
      Quantity: "10",
      FOBValueForeign: "1000.00",
    },
  ],
} as const;

export const sampleCancel = {
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-EXP-001",
  DeclarationType: "EXP",
  CancelReason: "Simulation test cancel",
} as const;

export const sampleGetXml = {
  ReferenceNumber: "REF-EXP-001",
} as const;

/** Sample for POST /api/v1/webhooks/send-mock. Backend forwards this to CLIENT_WEBHOOK_URL. Use PascalCase to match WebhookPayload (InterfaceId, MessageType, OMSCustomsId, ReferenceNumber, Status). */
export const sampleSendMockWebhook = {
  InterfaceId: "IF-001",
  MessageType: "XDCA" as const,
  OMSCustomsId: "OMS001",
  ReferenceNumber: "REF-001",
  Status: "0300",
  Message: "Export declaration accepted",
} as const;

export const sampleDcsaBooking = {
  documentParties: [],
} as Record<string, unknown>;

// Simulation config

export type SimulateErrorApiKey =
  | "EXPORT_DECL"
  | "VALIDATE_DECL"
  | "VALIDATE_WAIT_1M"
  | "CALCULATE"
  | "PRINT_DOCUMENT"
  | "SEND_EXPORT_DECL"
  | "EXPORT_ENTRIES_SUBMIT"
  | "CREATE_UPDATE_GCL"
  | "CREATE_UPDATE_SHORT"
  | "CANCEL_DOCUMENT"
  | "GET_XML"
  | "WEBHOOK_UPDATE_STATUS"
  | "DCSA_BOOKING_CREATE"
  | "DCSA_BOOKING_UPDATE"
  | "DCSA_BOOKING_AMEND"
  | "DCSA_BOOKING_CANCEL";

/** All SIMULATE_ERROR_* keys in display order (matches .env). */
export const SIMULATE_ERROR_KEYS_ORDER: SimulateErrorApiKey[] = [
  "EXPORT_DECL",
  "VALIDATE_DECL",
  "VALIDATE_WAIT_1M",
  "CALCULATE",
  "PRINT_DOCUMENT",
  "SEND_EXPORT_DECL",
  "EXPORT_ENTRIES_SUBMIT",
  "CREATE_UPDATE_GCL",
  "CREATE_UPDATE_SHORT",
  "CANCEL_DOCUMENT",
  "GET_XML",
  "WEBHOOK_UPDATE_STATUS",
  "DCSA_BOOKING_CREATE",
  "DCSA_BOOKING_UPDATE",
  "DCSA_BOOKING_AMEND",
  "DCSA_BOOKING_CANCEL",
];

/** DCSA booking simulate-error flags (Liner tab + Config tab). */
export const DCSA_SIMULATE_ERROR_KEYS: SimulateErrorApiKey[] = [
  "DCSA_BOOKING_CREATE",
  "DCSA_BOOKING_UPDATE",
  "DCSA_BOOKING_AMEND",
  "DCSA_BOOKING_CANCEL",
];

/** Liner outbound auto-webhooks (simulation_config; editable in Config UI). */
export interface LinerWebhookSimulationConfig {
  autoAfterAmend: boolean;
  autoAfterCancel: boolean;
  randomRejection: boolean;
}

export interface SimulationConfig {
  simulateRealSlowSpeedMs: number;
  simulateError: Record<SimulateErrorApiKey, boolean>;
  linerWebhook: LinerWebhookSimulationConfig;
  inboundApiKey: string;
  outboundWebhookApiKey: string;
  linerWebhookUrl: string;
}
